import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS, type TEAM_CATEGORY } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { assignedUsersQueryKeys } from './assignedUsersQueryKeys';
import { AssignedUsersToast } from './AssignedUsersToast';
import { invalidateAssignedUsers, isAssignmentConflict } from './assignmentConflict';
import { APICommunicator } from '~/api/APICommunicator';
import { useChainAwareInvalidation } from '~/hooks/useChainAwareInvalidation';

export const useChangeAssignedUserMutation = (loanRequestId: string) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const invalidateOnChainComplete = useChainAwareInvalidation();

    return useMutation({
        // category and name only feed the toasts, the backend derives the slot from the user's team
        mutationFn: ({ userId }: { category: TEAM_CATEGORY; name: string; userId: string }) =>
            APICommunicator.sendCommand({
                aggregateId: loanRequestId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.changeAssignedUser,
                payload: { userId },
            }),
        onError: (error: Error & { status?: number }, { category }) => {
            if (isAssignmentConflict(error)) {
                toast.error(t('LEADS.ASSIGNED_USERS.CHANGED_MEANWHILE'));
                return;
            }
            Sentry.captureException(error, { extra: { category } });
            toast.error(t('LEADS.ASSIGNED_USERS.CHANGE_ERROR', { team: t(`TEAMS.CATEGORIES.${category}`) }));
        },
        onSettled: (data) => {
            // failed: refetch now, a conflict means it changed meanwhile and other errors (e.g. timeout) may have persisted
            if (!data) {
                invalidateAssignedUsers(queryClient, loanRequestId);
                return;
            }
            invalidateOnChainComplete({ correlationId: data.id, queryKeys: assignedUsersQueryKeys(loanRequestId) });
        },
        onSuccess: (_data, { category, name }) => {
            toast.success(
                <AssignedUsersToast
                    body={t('LEADS.ASSIGNED_USERS.NOW_RESPONSIBLE', { name })}
                    title={t('LEADS.ASSIGNED_USERS.CHANGE_SUCCESS', { team: t(`TEAMS.CATEGORIES.${category}`) })}
                />,
            );
        },
    });
};
